import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { ouvrirFichier, getAchatsBureau, createAchatBureau, payAchatBureau, uploadFile, createAchatBureauCheckout } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  ShoppingBag,
  Euro,
  Calendar,
  Users,
  CreditCard,
  Loader2,
  CheckCircle,
  Clock,
  FileText,
  Divide,
  Upload,
  Download,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

export default function AchatsBureauPage() {
  const [achats, setAchats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payingStripeId, setPayingStripeId] = useState(null);
  const [formData, setFormData] = useState({
    description: "",
    montant_total: "",
    date: new Date().toISOString().slice(0, 10),
    justificatif: "",
  });
  const [uploadingFile, setUploadingFile] = useState(false);
  const [justificatifName, setJustificatifName] = useState("");
  const { isAdmin, user } = useAuth();

  useEffect(() => {
    loadAchats();
  }, []);

  const loadAchats = async () => {
    try {
      const response = await getAchatsBureau();
      setAchats(response.data);
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createAchatBureau({
        ...formData,
        montant_total: parseFloat(formData.montant_total),
      });
      toast.success("Achat enregistré - Répartition automatique effectuée");
      setIsDialogOpen(false);
      setFormData({
        description: "",
        montant_total: "",
        date: new Date().toISOString().slice(0, 10),
        justificatif: "",
      });
      setJustificatifName("");
      loadAchats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de création");
    }
  };

  const handlePay = async () => {
    if (!payTarget) return;
    try {
      await payAchatBureau(payTarget.achat_id, payTarget.ft_id);
      toast.success("Paiement enregistré");
      setPayTarget(null);
      loadAchats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handlePayStripe = async (achatId, ftId, montant) => {
    setPayingStripeId(`${achatId}-${ftId}`);
    try {
      const response = await createAchatBureauCheckout(achatId);
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création du paiement");
      setPayingStripeId(null);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Le fichier est trop volumineux (max 10MB)");
      return;
    }

    setUploadingFile(true);
    try {
      // Pass the file directly - uploadFile handles FormData internally
      const response = await uploadFile(file);
      const fileUrl = `/api/files/${response.data.file_id}`;
      setFormData({ ...formData, justificatif: fileUrl });
      setJustificatifName(file.name);
      toast.success("Justificatif uploadé");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Erreur d'upload du fichier");
    } finally {
      setUploadingFile(false);
    }
  };

  const getTotalStats = () => {
    let totalMontant = 0;
    let totalPaye = 0;
    let totalDu = 0;

    achats.forEach((achat) => {
      totalMontant += achat.montant_total;
      achat.repartitions.forEach((r) => {
        if (r.statut_paiement === "paye") {
          totalPaye += r.montant_du;
        } else {
          totalDu += r.montant_du;
        }
      });
    });

    return { totalMontant, totalPaye, totalDu };
  };

  const stats = getTotalStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="achats-bureau-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-primary">
            Achats Bureau
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestion des achats et répartition entre membres du bureau
          </p>
        </div>
        {isAdmin() && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-primary hover:bg-primary-hover text-white shadow-md"
                data-testid="add-achat-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Nouvel achat
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-primary">
                  Enregistrer un achat
                </DialogTitle>
                <DialogDescription>
                  Le montant sera réparti automatiquement entre les membres du bureau
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label className="text-secondary font-semibold">Description *</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="Ex: Matériel de cuisine"
                    data-testid="input-description"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">Montant total (€) *</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={formData.montant_total}
                      onChange={(e) => setFormData({ ...formData, montant_total: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="0.00"
                      data-testid="input-montant"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Date *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      data-testid="input-date"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Justificatif (fichier)</Label>
                  <div className="mt-1">
                    {justificatifName ? (
                      <div className="flex items-center gap-2 p-3 bg-status-success/10 border border-status-success/30 rounded-lg">
                        <FileText className="w-5 h-5 text-status-success" />
                        <span className="flex-1 text-sm text-secondary truncate">{justificatifName}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setFormData({ ...formData, justificatif: "" });
                            setJustificatifName("");
                          }}
                          className="text-status-error hover:text-status-error/80"
                        >
                          Supprimer
                        </Button>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-[#E5E0DC] rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                        {uploadingFile ? (
                          <>
                            <Loader2 className="w-5 h-5 text-primary animate-spin" />
                            <span className="text-sm text-muted-foreground">Upload en cours...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5 text-primary" />
                            <span className="text-sm text-muted-foreground">Cliquez pour uploader un fichier</span>
                          </>
                        )}
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          onChange={handleFileUpload}
                          disabled={uploadingFile}
                        />
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">PDF, Image ou Document (max 10MB)</p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="submit-achat-btn">
                    Enregistrer
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <ShoppingBag className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total achats</p>
                <p className="text-2xl font-heading font-bold text-secondary">{stats.totalMontant.toFixed(2)} €</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-status-success/10">
                <CheckCircle className="w-6 h-6 text-status-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payé</p>
                <p className="text-2xl font-heading font-bold text-status-success">{stats.totalPaye.toFixed(2)} €</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-status-warning/10">
                <Clock className="w-6 h-6 text-status-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">En attente</p>
                <p className="text-2xl font-heading font-bold text-status-warning">{stats.totalDu.toFixed(2)} €</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Achats List */}
      {achats.length > 0 ? (
        <Card className="bg-white border-[#E5E0DC]">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Historique des achats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="space-y-3">
              {achats.map((achat) => {
                const paidCount = achat.repartitions.filter((r) => r.statut_paiement === "paye").length;
                const totalCount = achat.repartitions.length;

                return (
                  <AccordionItem
                    key={achat.id}
                    value={achat.id}
                    className="border border-[#E5E0DC] rounded-lg overflow-hidden"
                    data-testid={`achat-${achat.id}`}
                  >
                    <AccordionTrigger className="px-4 py-3 hover:bg-[#F9F7F5] hover:no-underline">
                      <div className="flex items-center justify-between w-full mr-4">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <ShoppingBag className="w-5 h-5 text-primary" />
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-secondary">{achat.description}</p>
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                              <Calendar className="w-3 h-3" />
                              {format(parseISO(achat.date), "dd MMMM yyyy", { locale: fr })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-bold text-secondary">{achat.montant_total.toFixed(2)} €</p>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Divide className="w-3 h-3" /> {totalCount} membres
                            </p>
                          </div>
                          <Badge 
                            variant={paidCount === totalCount ? "default" : "secondary"}
                            className={paidCount === totalCount ? "bg-status-success" : "bg-status-warning text-white"}
                          >
                            {paidCount}/{totalCount}
                          </Badge>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="pt-2 border-t border-[#E5E0DC]">
                        <p className="text-sm font-semibold text-secondary mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4" /> Répartition ({achat.repartition_type})
                        </p>
                        <div className="space-y-2">
                          {achat.repartitions.map((r) => (
                            <div
                              key={r.food_truck_id}
                              className="flex items-center justify-between p-3 bg-[#F9F7F5] rounded-lg"
                            >
                              <div>
                                <p className="font-medium text-secondary">{r.nom_food_truck}</p>
                                <p className="text-sm text-muted-foreground">{r.montant_du.toFixed(2)} €</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {r.statut_paiement === "paye" ? (
                                  <Badge className="bg-status-success text-white">
                                    <CheckCircle className="w-3 h-3 mr-1" /> Payé
                                  </Badge>
                                ) : (
                                  <>
                                    <Badge variant="secondary" className="bg-status-warning text-white">
                                      <Clock className="w-3 h-3 mr-1" /> En attente
                                    </Badge>
                                    {/* Bouton paiement Stripe - visible pour le propriétaire du food truck */}
                                    {user?.food_truck_id === r.food_truck_id && (
                                      <Button
                                        size="sm"
                                        onClick={() => handlePayStripe(achat.id, r.food_truck_id, r.montant_du)}
                                        disabled={payingStripeId === `${achat.id}-${r.food_truck_id}`}
                                        className="bg-primary hover:bg-primary/90"
                                        data-testid={`pay-stripe-${achat.id}-${r.food_truck_id}`}
                                      >
                                        {payingStripeId === `${achat.id}-${r.food_truck_id}` ? (
                                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                        ) : (
                                          <CreditCard className="w-4 h-4 mr-1" />
                                        )}
                                        Payer en ligne
                                      </Button>
                                    )}
                                    {/* Bouton marquer payé - pour admin/bureau */}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setPayTarget({ achat_id: achat.id, ft_id: r.food_truck_id, montant: r.montant_du, nom_food_truck: r.nom_food_truck })}
                                      className="text-status-success border-status-success hover:bg-status-success hover:text-white"
                                      data-testid={`pay-manual-${achat.id}-${r.food_truck_id}`}
                                    >
                                      <CheckCircle className="w-4 h-4 mr-1" />
                                      Marquer payé
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {achat.justificatif && (
                          <div className="mt-4 pt-3 border-t border-[#E5E0DC]">
                            {achat.justificatif.startsWith('/api/files/') ? (
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}${achat.justificatif}`}
                                onClick={(e) => { e.preventDefault(); ouvrirFichier(achat.justificatif).catch(() => toast.error("Document indisponible ou accès refusé")); }}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
                                data-testid={`download-justificatif-${achat.id}`}
                              >
                                <Download className="w-4 h-4" />
                                Télécharger le justificatif
                              </a>
                            ) : (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <FileText className="w-4 h-4" />
                                <span>Justificatif: {achat.justificatif}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-12 text-center">
            <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl text-secondary mb-2">Aucun achat enregistré</h3>
            <p className="text-muted-foreground">
              Les achats du bureau seront automatiquement répartis entre les membres
            </p>
          </CardContent>
        </Card>
      )}

      {/* Pay Confirmation */}
      <AlertDialog open={!!payTarget} onOpenChange={() => setPayTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-status-success" />
              Confirmer le paiement
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Vous allez marquer ce paiement comme effectué :</p>
              {payTarget && (
                <div className="bg-[#F9F7F5] p-3 rounded-lg mt-2">
                  <p className="font-medium text-secondary">{payTarget.nom_food_truck}</p>
                  <p className="text-lg font-bold text-primary">{payTarget.montant?.toFixed(2)} €</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePay}
              className="bg-status-success hover:bg-status-success/90"
              data-testid="confirm-pay-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirmer le paiement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
